import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { PaywallDialog } from "@/components/PaywallDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  FileSignature, ArrowLeft, ArrowRight, Download, Loader2, Trash2, Plus, Check, Layers, FolderLock, Pencil, Save, X,
} from "lucide-react";
import { cn } from "@/lib/utils";

const STEPS = ["Type", "Vos infos", "Votre situation", "Courrier"];

export default function Letters() {
  const { user, refresh } = useAuth();
  const [types, setTypes] = useState([]);
  const [list, setList] = useState([]);
  const [mode, setMode] = useState("list"); // list | wizard | result
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    type: "", expediteur: "", adresse_expediteur: "", destinataire: "",
    reference: "", ville: "", details: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const [paywallReason, setPaywallReason] = useState("");
  const [dossierOpen, setDossierOpen] = useState(false);
  const [vaultItems, setVaultItems] = useState([]);
  const [selectedVault, setSelectedVault] = useState([]);
  const [buildingDossier, setBuildingDossier] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editObjet, setEditObjet] = useState("");
  const [editCorps, setEditCorps] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const loadList = () => api.get("/letters").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/letters/types/list").then((r) => setTypes(r.data.items)).catch(() => {});
    loadList();
  }, []);

  const startWizard = () => {
    setForm({ type: "", expediteur: "", adresse_expediteur: "", destinataire: "", reference: "", ville: "", details: "" });
    setStep(0); setResult(null); setMode("wizard");
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/letters/generate", form);
      setResult(data);
      setMode("result");
      toast.success("Courrier généré !");
      loadList(); refresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 402) {
        setPaywallReason(detail?.message || "Limite atteinte.");
        setPaywall(true);
      } else {
        toast.error(formatApiError(detail) || "La génération a échoué.");
      }
    } finally { setLoading(false); }
  };

  const openLetter = (l) => { setResult(l); setEditing(false); setMode("result"); };
  const remove = async (id) => { await api.delete(`/letters/${id}`); toast.success("Courrier supprimé."); loadList(); };
  const dl = (l) => downloadFile(`/letters/${l.id}/pdf`, `courrier_${l.id.slice(0, 6)}.pdf`);

  const startEdit = () => {
    setEditObjet(result.objet || "");
    setEditCorps(result.corps || "");
    setEditing(true);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/letters/${result.id}`, { objet: editObjet, corps: editCorps });
      setResult(data);
      setEditing(false);
      loadList();
      toast.success("Courrier mis à jour.");
    } catch {
      toast.error("La sauvegarde a échoué.");
    } finally { setSavingEdit(false); }
  };

  const openDossier = () => {
    if (!user?.is_premium) {
      setPaywallReason("Le dossier assemblé est réservé aux abonnés Premium.");
      setPaywall(true);
      return;
    }
    setSelectedVault([]);
    api.get("/vault").then((r) => setVaultItems(r.data)).catch(() => {});
    setDossierOpen(true);
  };

  const toggleVault = (id) =>
    setSelectedVault((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const buildDossier = async () => {
    setBuildingDossier(true);
    try {
      const res = await api.post(`/letters/${result.id}/dossier`, { vault_ids: selectedVault }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `dossier_${result.id.slice(0, 6)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Dossier assemblé téléchargé !");
      setDossierOpen(false);
    } catch {
      toast.error("La création du dossier a échoué.");
    } finally { setBuildingDossier(false); }
  };

  // ---------- LIST ----------
  if (mode === "list") {
    return (
      <div className="space-y-6 adminia-fade-in">
        <PaywallDialog open={paywall} onOpenChange={setPaywall} reason={paywallReason} />
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl">Mes courriers</h1>
            <p className="text-muted-foreground mt-1">Générez un courrier officiel en français parfait.</p>
          </div>
          <Button className="rounded-full bg-brand hover:bg-brand/90 gap-2 shrink-0" onClick={startWizard} data-testid="letter-new-btn">
            <Plus className="h-4 w-4" /> Nouveau
          </Button>
        </div>
        {list.length === 0 ? (
          <Card className="p-12 text-center border-dashed">
            <div className="h-16 w-16 rounded-2xl bg-brand/10 flex items-center justify-center mx-auto mb-4">
              <FileSignature className="h-8 w-8 text-brand" />
            </div>
            <p className="font-medium">Aucun courrier pour l'instant</p>
            <Button className="rounded-full bg-brand hover:bg-brand/90 mt-4" onClick={startWizard} data-testid="letter-empty-cta">
              Rédiger mon premier courrier
            </Button>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {list.map((l) => (
              <Card key={l.id} className="p-5 hover:shadow-md transition-shadow" data-testid={`letter-item-${l.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-xs font-semibold text-brand bg-brand/10 px-2 py-0.5 rounded-full">{l.type_label}</span>
                    <p className="font-medium mt-2 truncate">{l.objet}</p>
                    <p className="text-xs text-muted-foreground mt-1">{new Date(l.created_at).toLocaleDateString("fr-FR")}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={() => openLetter(l)} data-testid={`letter-open-${l.id}`}>Ouvrir</Button>
                  <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => dl(l)} data-testid={`letter-pdf-${l.id}`}><Download className="h-3.5 w-3.5" /> PDF</Button>
                  <Button size="sm" variant="ghost" className="rounded-full text-danger" onClick={() => remove(l.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- RESULT ----------
  if (mode === "result" && result) {
    return (
      <div className="space-y-5 adminia-fade-in max-w-3xl mx-auto">
        <Button variant="ghost" onClick={() => setMode("list")} className="gap-2 -ml-2"><ArrowLeft className="h-4 w-4" /> Retour</Button>
        <Card className="p-6 sm:p-10 bg-white">
          <div className="flex items-start justify-between gap-3 mb-4">
            <p className="text-xs font-semibold text-brand uppercase tracking-wide">{result.type_label}</p>
            {!editing ? (
              <Button size="sm" variant="outline" className="rounded-full gap-1.5 shrink-0" onClick={startEdit} data-testid="letter-edit-btn">
                <Pencil className="h-3.5 w-3.5" /> Modifier
              </Button>
            ) : (
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="ghost" className="rounded-full gap-1.5" onClick={() => setEditing(false)} data-testid="letter-edit-cancel">
                  <X className="h-3.5 w-3.5" /> Annuler
                </Button>
                <Button size="sm" className="rounded-full gap-1.5 bg-brand hover:bg-brand/90" onClick={saveEdit} disabled={savingEdit} data-testid="letter-edit-save">
                  {savingEdit ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Enregistrer
                </Button>
              </div>
            )}
          </div>
          {editing ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-objet">Objet</Label>
                <Input id="edit-objet" value={editObjet} className="h-11 mt-1" onChange={(e) => setEditObjet(e.target.value)} data-testid="letter-edit-objet" />
              </div>
              <div>
                <Label htmlFor="edit-corps">Corps du courrier</Label>
                <Textarea id="edit-corps" value={editCorps} rows={16} className="mt-1 font-sans leading-relaxed" onChange={(e) => setEditCorps(e.target.value)} data-testid="letter-edit-corps" />
              </div>
            </div>
          ) : (
            <>
              <p className="font-heading font-bold text-xl mb-6">Objet : {result.objet}</p>
              <div className="whitespace-pre-line leading-relaxed text-[15px]">{result.corps}</div>
              {result.pieces_jointes?.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <p className="font-semibold text-sm mb-2">Pièces jointes :</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {result.pieces_jointes.map((p, i) => <li key={i}>— {p}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}
        </Card>
        <div className="grid sm:grid-cols-2 gap-3">
          <Button className="rounded-full h-12 bg-brand hover:bg-brand/90 gap-2" onClick={() => dl(result)} data-testid="letter-download-pdf">
            <Download className="h-5 w-5" /> Télécharger en PDF
          </Button>
          <Button variant="outline" className="rounded-full h-12 gap-2 border-brand text-brand hover:bg-brand hover:text-white" onClick={openDossier} data-testid="dossier-btn">
            <Layers className="h-5 w-5" /> Dossier assemblé {!user?.is_premium && "(Premium)"}
          </Button>
        </div>

        <Dialog open={dossierOpen} onOpenChange={setDossierOpen}>
          <DialogContent data-testid="dossier-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Assembler le dossier</DialogTitle>
              <DialogDescription>
                Sélectionnez les justificatifs de votre coffre-fort à joindre au courrier. Tout sera fusionné en un seul PDF.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-72 overflow-auto space-y-2 my-2">
              {vaultItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FolderLock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Votre coffre-fort est vide.</p>
                </div>
              ) : vaultItems.map((it) => (
                <label key={it.id} className="flex items-center gap-3 p-3 rounded-lg bg-surface cursor-pointer" data-testid={`dossier-item-${it.id}`}>
                  <Checkbox checked={selectedVault.includes(it.id)} onCheckedChange={() => toggleVault(it.id)} />
                  <span className="text-sm font-medium truncate">{it.label}</span>
                </label>
              ))}
            </div>
            <DialogFooter>
              <Button className="w-full rounded-full bg-brand hover:bg-brand/90 gap-2" onClick={buildDossier} disabled={buildingDossier} data-testid="dossier-build-btn">
                {buildingDossier ? <Loader2 className="h-5 w-5 animate-spin" /> : <Layers className="h-5 w-5" />}
                Télécharger le dossier ({selectedVault.length} justificatif{selectedVault.length > 1 ? "s" : ""})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ---------- WIZARD ----------
  return (
    <div className="space-y-6 adminia-fade-in max-w-2xl mx-auto">
      <PaywallDialog open={paywall} onOpenChange={setPaywall} reason={paywallReason} />
      <Button variant="ghost" onClick={() => setMode("list")} className="gap-2 -ml-2"><ArrowLeft className="h-4 w-4" /> Annuler</Button>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={cn("h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0",
              i < step ? "bg-success text-white" : i === step ? "bg-brand text-white" : "bg-muted text-muted-foreground")}>
              {i < step ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={cn("text-xs hidden sm:block", i === step ? "font-semibold text-brand" : "text-muted-foreground")}>{s}</span>
            {i < STEPS.length - 1 && <div className="h-0.5 flex-1 bg-muted" />}
          </div>
        ))}
      </div>

      <Card className="p-6">
        {step === 0 && (
          <div className="space-y-3">
            <h2 className="font-heading font-semibold text-lg">Quel type de courrier ?</h2>
            <div className="grid gap-3">
              {types.map((t) => (
                <button key={t.id} onClick={() => set("type", t.id)}
                  className={cn("text-left p-4 rounded-xl border transition-all",
                    form.type === t.id ? "border-brand bg-brand/5 ring-1 ring-brand" : "border-border hover:border-brand/40")}
                  data-testid={`letter-type-${t.id}`}>
                  <p className="font-medium">{t.label}</p>
                  <p className="text-sm text-muted-foreground">{t.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="font-heading font-semibold text-lg">Vos informations</h2>
            <div><Label>Votre nom / prénom</Label><Input value={form.expediteur} className="h-11 mt-1" onChange={(e) => set("expediteur", e.target.value)} data-testid="wiz-expediteur" /></div>
            <div><Label>Votre adresse</Label><Input value={form.adresse_expediteur} className="h-11 mt-1" onChange={(e) => set("adresse_expediteur", e.target.value)} data-testid="wiz-adresse" /></div>
            <div><Label>Votre ville</Label><Input value={form.ville} className="h-11 mt-1" onChange={(e) => set("ville", e.target.value)} data-testid="wiz-ville" /></div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <h2 className="font-heading font-semibold text-lg">La situation</h2>
            <div><Label>Organisme destinataire</Label><Input value={form.destinataire} className="h-11 mt-1" placeholder="Ex : CAF de Paris" onChange={(e) => set("destinataire", e.target.value)} data-testid="wiz-destinataire" /></div>
            <div><Label>Référence dossier (optionnel)</Label><Input value={form.reference} className="h-11 mt-1" onChange={(e) => set("reference", e.target.value)} data-testid="wiz-reference" /></div>
            <div><Label>Expliquez votre situation</Label>
              <Textarea value={form.details} rows={5} className="mt-1" placeholder="Décrivez ce que vous voulez demander ou contester…"
                onChange={(e) => set("details", e.target.value)} data-testid="wiz-details" /></div>
          </div>
        )}
        {step === 3 && (
          <div className="text-center py-6">
            {loading ? (
              <>
                <Loader2 className="h-10 w-10 text-brand mx-auto animate-spin mb-4" />
                <p className="font-heading font-semibold text-lg">Rédaction en cours…</p>
              </>
            ) : (
              <>
                <div className="h-14 w-14 rounded-2xl bg-brand/10 flex items-center justify-center mx-auto mb-4">
                  <FileSignature className="h-7 w-7 text-brand" />
                </div>
                <p className="font-heading font-semibold text-lg mb-1">Tout est prêt</p>
                <p className="text-sm text-muted-foreground mb-5">Générons votre courrier officiel.</p>
                <Button className="rounded-full h-12 px-8 bg-brand hover:bg-brand/90" onClick={generate} data-testid="wiz-generate">Générer mon courrier</Button>
              </>
            )}
          </div>
        )}

        {step < 3 && (
          <div className="flex justify-between mt-6">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>Précédent</Button>
            <Button className="rounded-full bg-brand hover:bg-brand/90 gap-2"
              disabled={step === 0 && !form.type}
              onClick={() => setStep((s) => s + 1)} data-testid="wiz-next">
              Suivant <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
